import { type CustomerSummary, type PaymentMethod, api } from '@/api/client'
import { CurrencyAmount } from '@/components/shared/CurrencyAmount'
import { CustomerPicker } from '@/components/shared/CustomerPicker'
import { InvoiceStatusBadge } from '@/components/shared/InvoiceStatusBadge'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { useIsReadOnly } from '@/hooks/useSubscription'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import type { NavigateOptions } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as appLayoutRoute } from '../layout'

interface PaymentNewSearch {
  invoice_id?: string
  customer_id?: string
  amount?: number
}

export const Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/finance/payments/new',
  validateSearch: (search: Record<string, unknown>): PaymentNewSearch => {
    const rawAmount = search.amount
    let amount: number | undefined
    if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
      amount = rawAmount
    } else if (typeof rawAmount === 'string' && rawAmount !== '') {
      const n = Number(rawAmount)
      amount = Number.isFinite(n) ? n : undefined
    }
    return {
      invoice_id: typeof search.invoice_id === 'string' ? search.invoice_id : undefined,
      customer_id: typeof search.customer_id === 'string' ? search.customer_id : undefined,
      amount,
    }
  },
  component: RecordPaymentPage,
})

const METHODS: PaymentMethod[] = ['bank_transfer', 'wave', 'orange_money', 'check', 'cash', 'other']

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

// The backend stores money in centimes (1 XOF = 100 centimes).
// All form fields in this page are in whole XOF — we convert to centimes
// exactly once, on submit. Pre-fill values coming from the invoice
// detail page arrive in centimes and are divided by 100 to hydrate the
// inputs. Keeping the form in major units means users never see giant
// unformatted integers and the recorded amount matches what they typed.
const XOF_TO_CENTIMES = 100

function centimesToXOF(centimes: number | undefined): number {
  if (!centimes || Number.isNaN(centimes)) return 0
  return Math.round(centimes / XOF_TO_CENTIMES)
}

function xofToCentimes(xof: number): number {
  if (!xof || Number.isNaN(xof)) return 0
  return Math.round(xof * XOF_TO_CENTIMES)
}

function RecordPaymentPage() {
  const { t } = useTranslation('finance')
  const toast = useToast()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const search = Route.useSearch() as unknown as PaymentNewSearch

  // L6 — Bloque les mutations quand le tenant est en dunning restricted/suspended.
  // Pattern à propager sur les autres écrans de création en L7 :
  //   const readOnly = useIsReadOnly()
  //   <Button disabled={readOnly || ...} title={readOnly ? '...' : undefined} />
  const readOnly = useIsReadOnly()

  const [customer, setCustomer] = useState<CustomerSummary | null>(null)
  // Amount / allocations are in whole XOF in the UI; converted on submit.
  const [amountXOF, setAmountXOF] = useState<number>(centimesToXOF(search.amount))

  // Pre-select customer when the form is opened from the invoice detail
  // page (customer_id arrives in the URL). We fetch the full customer
  // once so the CustomerPicker receives a CustomerSummary and stays in
  // sync. Without this the user would see an empty picker even though
  // the amount + allocations are pre-filled, forcing a redundant pick.
  const preselectCustomerQuery = useQuery({
    queryKey: ['customers', 'get', search.customer_id],
    enabled: !!search.customer_id && !customer,
    queryFn: () => api.customers.get(search.customer_id as string),
  })
  useEffect(() => {
    if (customer || !preselectCustomerQuery.data) return
    const c = preselectCustomerQuery.data
    setCustomer({ id: c.id, ref: c.ref, legal_name: c.legal_name, tier: c.tier, status: c.status })
  }, [preselectCustomerQuery.data, customer])
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer')
  const [reference, setReference] = useState('')
  const [receivedAt, setReceivedAt] = useState(todayStr())
  const [notes, setNotes] = useState('')
  const [allocMapXOF, setAllocMapXOF] = useState<Record<string, number>>(() => {
    if (search.invoice_id && search.amount)
      return { [search.invoice_id]: centimesToXOF(search.amount) }
    return {}
  })

  const invoicesQuery = useQuery({
    queryKey: ['finance', 'invoices', 'customer', customer?.id],
    enabled: !!customer,
    queryFn: () => api.finance.invoices.list({ customer_id: customer!.id, limit: 50 }),
  })
  const openInvoices = useMemo(
    () =>
      (invoicesQuery.data?.data ?? []).filter(
        (i) => i.balance > 0 && i.status !== 'voided' && i.status !== 'paid',
      ),
    [invoicesQuery.data],
  )

  const totalAllocatedXOF = Object.values(allocMapXOF).reduce((acc, v) => acc + (v || 0), 0)
  const remainingXOF = amountXOF - totalAllocatedXOF

  const mutation = useMutation({
    mutationFn: () =>
      api.finance.payments.record({
        customer_id: customer!.id,
        amount: xofToCentimes(amountXOF),
        method,
        reference: reference || undefined,
        received_at: receivedAt,
        notes: notes || undefined,
        allocations: Object.entries(allocMapXOF)
          .filter(([, v]) => v > 0)
          .map(([invoice_id, xof]) => ({ invoice_id, amount: xofToCentimes(xof) })),
      }),
    onSuccess: () => {
      toast.show({ title: t('payment.record'), variant: 'success' })
      void qc.invalidateQueries({ queryKey: ['finance'] })
      navigate({ to: '/finance' } as unknown as NavigateOptions)
    },
    onError: (err: Error) => toast.show({ title: err.message, variant: 'error' }),
  })

  const canSubmit = customer && amountXOF > 0 && !!receivedAt && totalAllocatedXOF <= amountXOF

  return (
    <div className="space-y-6">
      <PageHeader title={t('payment.record')} />

      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('invoice.customer')}</Label>
              <CustomerPicker value={customer} onChange={setCustomer} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('payment.received_at')}</Label>
              <Input
                type="date"
                value={receivedAt}
                onChange={(e) => setReceivedAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                {t('payment.amount')}{' '}
                <span className="text-xs font-normal text-muted-foreground">(XOF)</span>
              </Label>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={amountXOF || ''}
                onChange={(e) => setAmountXOF(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('payment.method')}</Label>
              <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {t(`method.${m}`)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('payment.reference')}</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t('payment.notes')}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </div>
          </div>
        </CardContent>
      </Card>

      {customer ? (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div className="text-base font-semibold">{t('payment.allocate')}</div>
              <div className="text-xs text-muted-foreground">
                {t('payment.remaining')}:{' '}
                <CurrencyAmount centimes={xofToCentimes(remainingXOF)} size="sm" />
              </div>
            </div>
            {invoicesQuery.isLoading ? (
              <div className="px-6 py-4 text-sm text-muted-foreground">…</div>
            ) : openInvoices.length === 0 ? (
              <div className="px-6 py-4 text-sm text-muted-foreground">{t('invoice.empty')}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/60 text-xs uppercase text-muted-foreground">
                    <th className="px-6 py-3 text-left">{t('invoice.ref')}</th>
                    <th className="px-6 py-3">{t('filters.status')}</th>
                    <th className="px-6 py-3 text-right">{t('invoice.balance')}</th>
                    <th className="px-6 py-3 text-right">{t('payment.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {openInvoices.map((inv) => {
                    const balanceXOF = centimesToXOF(inv.balance)
                    const currentXOF = allocMapXOF[inv.id] ?? 0
                    return (
                      <tr key={inv.id} className="border-t border-border">
                        <td className="px-6 py-3 font-medium">{inv.ref}</td>
                        <td className="px-6 py-3">
                          <InvoiceStatusBadge status={inv.status} />
                        </td>
                        <td className="px-6 py-3 text-right">
                          <CurrencyAmount centimes={inv.balance} size="sm" />
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Input
                            type="number"
                            inputMode="numeric"
                            step={1}
                            min={0}
                            max={balanceXOF}
                            value={currentXOF || ''}
                            onChange={(e) => {
                              const raw = Number(e.target.value)
                              const v = Math.max(0, Math.min(balanceXOF, raw))
                              setAllocMapXOF((prev) => ({ ...prev, [inv.id]: v }))
                            }}
                            className="w-32 text-right"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/finance' } as unknown as NavigateOptions)}
        >
          {t('invoice.void_modal.cancel')}
        </Button>
        <Button
          disabled={!canSubmit || mutation.isPending || readOnly}
          onClick={() => mutation.mutate()}
          title={
            readOnly
              ? 'Compte en lecture seule — régularisez votre abonnement pour reprendre les enregistrements.'
              : undefined
          }
        >
          {t('payment.record')}
        </Button>
      </div>
    </div>
  )
}
